import React, { useState, useEffect, useCallback } from "react";
import { PageFormLayout, FormActions, Button } from "./ui";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { MDBRow, MDBCol, MDBSpinner } from "mdb-react-ui-kit";
import { api, getTenantApiScope } from "../services";
import { useTranslation } from "react-i18next";
import NativeInput from "./NativeInput";

/** GET /stores/:id via StoreControllerUpdated returns `{ success, data }`; legacy handlers may return the store document at the root. */
function unwrapStoreDetailsBody(body) {
  if (body && typeof body === "object" && body.success === true && body.data != null) {
    return body.data;
  }
  return body;
}

function EditStore() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const queryStoreId =
    searchParams.get("storeId") || searchParams.get("store_id") || "";
  const scopeStoreId = getTenantApiScope().storeId || "";
  const storeId =
    location.state?.storeId || queryStoreId || scopeStoreId || null;

  const [storeData, setStoreData] = useState({ name: "", email: "" });
  const [fieldErrors, setFieldErrors] = useState({ name: "", email: "" });
  const [loadingStore, setLoadingStore] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCancel = () => {
    navigate("/store&branches-overview");
  };

  const loadStoreDetails = useCallback(
    async ({ showSpinner } = { showSpinner: true }) => {
      if (!storeId) return;
      try {
        if (showSpinner) setLoadingStore(true);
        const token = localStorage.getItem("token");
        const response = await api.get(`/stores/${storeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = unwrapStoreDetailsBody(response.data);
        if (!payload || typeof payload !== "object") {
          toast.error(`${t("Failed to load store details.")}`);
          return;
        }
        const storeEmail =
          payload.email != null && String(payload.email).trim() !== ""
            ? String(payload.email).trim()
            : "";
        const adminEmail =
          typeof payload.admin === "object" && payload.admin?.email
            ? String(payload.admin.email).trim()
            : "";
        setStoreData({
          name: payload.name ?? "",
          email: storeEmail || adminEmail,
        });
        setFieldErrors({ name: "", email: "" });
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          `${t("Failed to load store details.")}`;
        toast.error(msg);
      } finally {
        if (showSpinner) setLoadingStore(false);
      }
    },
    [storeId, t]
  );

  useEffect(() => {
    if (!storeId) {
      setLoadingStore(false);
      return undefined;
    }
    loadStoreDetails({ showSpinner: true });
    return undefined;
  }, [storeId, loadStoreDetails]);

  const isValidEmail = (val) => /\S+@\S+\.\S+/.test(val);

  const validateAll = () => {
    const errs = { name: "", email: "" };
    if (!storeData.name.trim()) errs.name = t("Store name is required");
    if (!storeData.email.trim()) errs.email = t("Email is required");
    else if (!isValidEmail(storeData.email.trim()))
      errs.email = t("Please enter a valid email address");
    setFieldErrors(errs);
    return !errs.name && !errs.email;
  };

  const handleStoreUpdate = async () => {
    if (!validateAll()) return;

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await api.put(
        `/stores/${storeId}`,
        {
          name: storeData.name.trim(),
          email: storeData.email.trim().toLowerCase(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`${t("Store updated successfully!")}`);
      setTimeout(() => {
        navigate("/store&branches-overview");
      }, 1500);
    } catch (err) {
      toast.error(`${t("Failed to update store.")}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setStoreData((prev) => ({ ...prev, [field]: value }));
    if (field === "name") {
      setFieldErrors((prev) => ({
        ...prev,
        name: value.trim() ? "" : t("Store name is required"),
      }));
    }
    if (field === "email") {
      const val = value.trim();
      setFieldErrors((prev) => ({
        ...prev,
        email: !val
          ? t("Email is required")
          : isValidEmail(val)
            ? ""
            : t("Please enter a valid email address"),
      }));
    }
  };

  if (!storeId) {
    return (
      <PageFormLayout rootClass="edit-store" title={t("editstore")}>
        <p className="mb-4">
          {t(
            "editStoreNoStoreContext",
            "No store is selected. Open Edit store from Stores & branches overview, or open this page with ?storeId= in the URL. If you use the dashboard store selector, it will be used automatically when nothing else is set."
          )}
        </p>
        <div className="oh-form-actions">
          <Button
            color="primary"
            size="md"
            onClick={() => navigate("/store&branches-overview")}
          >
            {t("goToStoresOverview", "Go to Stores & branches")}
          </Button>
        </div>
      </PageFormLayout>
    );
  }

  if (loadingStore) {
    return (
      <PageFormLayout rootClass="edit-store" title={t("editstore")}>
        <div className="d-flex justify-content-center align-items-center py-5">
          <MDBSpinner role="status" />
        </div>
      </PageFormLayout>
    );
  }

  return (
    <PageFormLayout rootClass="edit-store" title={t("editstore")}>
      <MDBRow className="g-3">
        <MDBCol md="6">
          <NativeInput
            label={
              <span>
                {t("storename")} <span className="text-danger">*</span>
              </span>
            }
            value={storeData.name}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            invalid={!!fieldErrors.name}
            feedback={fieldErrors.name}
          />
        </MDBCol>
        <MDBCol md="6">
          <NativeInput
            label={
              <span>
                {t("emailaddress")}{" "}
                <span className="text-danger">*</span>
              </span>
            }
            type="email"
            value={storeData.email}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            invalid={!!fieldErrors.email}
            feedback={fieldErrors.email}
          />
        </MDBCol>
      </MDBRow>

      <FormActions
        isRtl={isArabic}
        cancelLabel={t("cancel")}
        saveLabel={t("savestore")}
        savingLabel={t("saving")}
        onCancel={handleCancel}
        onSave={handleStoreUpdate}
        saving={saving}
      />
    </PageFormLayout>
  );
}

export default EditStore;
